// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Putting the machine in the player's room (M5-14 … M5-18, XR-002, XR-003).
//
// The ritual, as a state machine: scan, aim, place, adjust, play. It is the first thing a new MR
// player does and it has to work the first time, in a room the developer has never seen.
//
// **Play is what commits it.** 06 §3: "Grabbing the plunger or a flipper button — rather than a
// handle — ends the placement ritual and starts play." There is no Done button, and there must
// not be: a Done button is a thing to find, and the thing a player wants to do next is play. The
// handles stay live until the player reaches for the machine instead of for a corner of it.
//
// **A failed re-localisation goes back to the ritual, and never guesses.** An anchor that cannot
// be found again means the room has changed or the headset has lost its map; putting the machine
// where it was last time would put it through a wall. Safe placement is one pinch and a known
// state, and that is strictly better than a confident wrong answer.
//
// **Room fit recommends; it never refuses.** The check exists because "not enough space" was the
// complaint reviewers levelled at the competition's MR mode, and the answer to it is to *offer*
// tabletop rather than to *impose* it. A player who wants the full cabinet in a small room may
// have a reason — they might be about to move a chair.
//
// **Only the player moves the machine (XR-002c, 06 §3.2).** The session's first head pose stands
// the machine in front of the player, and that is the first placement: from then on it moves for
// the reasons the requirement lists — Meta's recentre, Re-place / Bring it to you / Face Me / a
// scale change from the wrist menu, a drag by hand — and for nothing the player did not do. M5-33
// had the machine *follow* the head for as long as the ritual was scanning, so that a player
// standing away from an invented tracking origin was not looking at a cabinet across the room; the
// headset's report on 13 Sep 2026 was "the actual pinball machine moves with head", because the
// ritual is scanning on every frame the gaze ray misses the floor, and it was placing on the first
// aiming frame only because a relaxed hand reads as pinched (7d5ac00 stopped that, and uncovered
// this). The aiming phase *is* the machine following the gaze — the native client found the same
// thing and wrote it down under M7-41. So the browser's first placement is the head-referenced one
// Instant Play makes, the reticle is what **Re-place** brings back, and `observe` moves nothing.

import {
  clampScale,
  defaultPlacement,
  TABLETOP_DEFAULT,
  yawFacing,
  type Placement,
  type PlacementScale,
} from './placement'
import {
  clampToReach,
  levelForward,
  placementInFrontOf,
  squareToRoom,
  type LevelDirection,
} from './viewerRay'
import { barIsPlayable, RESTORED_BAR_BELOW_EYE, SUNKEN_TOLERANCE } from './anchors'

/** Where the ritual is. */
export type RitualPhase =
  /** Looking for a surface. The reticle is not showing because there is nothing to show it on. */
  | 'scanning'
  /** A surface is tracked, the reticle is on it and shimmering, waiting for a pinch. */
  | 'aiming'
  /** Placed. Handles are live and the machine can be rotated, resized or re-placed. */
  | 'adjusting'
  /** The player reached for the machine. The ritual is over and the game has the controls. */
  | 'playing'

/** A hit-test result, reduced to what the ritual needs. */
export interface SurfaceHit {
  readonly x: number
  readonly y: number
  readonly z: number
  /** Yaw of the surface's dominant axis, if the runtime reports one. */
  readonly yaw?: number
}

/**
 * The floor space a full-size cabinet plus a standing player needs, metres (06 §3).
 *
 * 1.0 × 1.4. The cabinet itself is 0.72 × 1.30; the extra 0.1 m of width is elbow room at the
 * flipper buttons and the extra 0.1 m of depth is the player, who has to stand somewhere. It is
 * not generous, deliberately — the check is there to catch a room that genuinely cannot take a
 * cabinet, not to talk people out of one.
 */
export const FULL_SCALE_FLOOR = { width: 1.0, depth: 1.4 } as const

/**
 * How close to the floor a surface has to be to count as the floor, metres.
 *
 * Generous, because it is only asked in order to *stop refusing*: a room mesh's floor is not
 * perfectly flat, a rug is a surface, and the cost of calling a low step "the floor" is that a
 * machine may stand on it. The cost of missing the floor is a player who cannot finish the ritual.
 *
 * The same 0.05 as `PlacementRitual.FloorTolerance` on the Unity side, and the same number
 * `SUNKEN_TOLERANCE` names from underneath — one definition, aliased, so the product cannot
 * end up with two floors.
 */
export const FLOOR_TOLERANCE = SUNKEN_TOLERANCE

/**
 * How far one press of a rotation nudge turns the machine, radians.
 *
 * **Five degrees, because 06 §5 says "5° rotation nudges" and because the alternative is a drag.**
 * A player squaring a cabinet up against a wall wants a repeatable amount, not a gesture: seventy-
 * two presses is a full turn, which sounds like a lot until you notice nobody ever wants one — the
 * placement ritual already faced it roughly the right way and this is the last few degrees.
 */
export const NUDGE_RADIANS = (5 * Math.PI) / 180

/**
 * The one sentence a player gets when the reticle will not settle on the furniture they are
 * looking at. Held here so the shell and the tests agree on it, as `SHORT_ROOM_ADVICE` is.
 */
export const AIM_TOO_HIGH_LINE =
  'That surface is too high for this machine — aim at the floor, or switch to tabletop size.'

/** What the room-fit check concluded. */
export interface RoomFit {
  readonly fitsFullScale: boolean
  /** The scale to preselect. Tabletop when the floor is short, full otherwise. */
  readonly recommended: PlacementScale
  /** One sentence for the player, or undefined when there is nothing to say. */
  readonly advice?: string
}

/**
 * Does the play area take a full-size cabinet?
 *
 * `undefined` bounds — a runtime that granted no `bounded-floor` — recommends full scale and says
 * nothing. Guessing "probably too small" from an absence would push every player on a runtime
 * without guardian bounds into tabletop, which is the opposite of the intent.
 */
export function roomFit(bounds: { readonly width: number; readonly depth: number } | undefined): RoomFit {
  if (bounds === undefined) return { fitsFullScale: true, recommended: 'full' }
  const fits = bounds.width >= FULL_SCALE_FLOOR.width && bounds.depth >= FULL_SCALE_FLOOR.depth
  if (fits) return { fitsFullScale: true, recommended: 'full' }
  return {
    fitsFullScale: false,
    recommended: 'tabletop',
    advice: 'Your play space is a little short for a full-size cabinet — tabletop is set up for you, and full scale is still there if you want it.',
  }
}

/** What the ritual is showing right now. */
export interface RitualView {
  readonly phase: RitualPhase
  /** Where to draw the reticle, or undefined while scanning. */
  readonly reticle: SurfaceHit | undefined
  /** True while a surface is tracked, which is what the shimmer means. */
  readonly shimmer: boolean
  readonly placement: Placement
  readonly scaleMode: PlacementScale
  readonly fit: RoomFit
}

export interface PlacementRitualOptions {
  /**
   * How long the cabinet is, front to back, metres.
   *
   * **Required, and not defaulted.** Every reach decision in here is derived from the body — how
   * far the near edge is from the centre is the whole difference between a full-size machine and
   * a tabletop one — and a ritual holding a guessed body would clamp placements against a cabinet
   * the renderer is not drawing. That is the same defect class `cabinetDimensionsFor` exists to
   * prevent, so this asks for the real number rather than keeping a second copy of 1.295.
   */
  readonly cabinetLength: number
  /**
   * How high the lockdown bar stands above the cabinet's feet at full scale, metres.
   *
   * **Required, and not defaulted, for the same reason `cabinetLength` is.** The height rule in
   * `playable` is derived from the body, and a ritual holding a guessed bar would refuse surfaces
   * on behalf of a cabinet the renderer is not drawing.
   */
  readonly barHeight: number
  /** The play area, if the runtime granted bounds. Drives the room-fit recommendation. */
  readonly bounds?: { readonly width: number; readonly depth: number }
  /** A placement recovered from a persisted anchor, if there was one and it re-localised. */
  readonly restored?: Placement
}

/**
 * Where the player is, as the ritual last saw them.
 *
 * Kept because three of the ritual's answers are only correct relative to a person — recenter,
 * a lost anchor, and the reach clamp — and the ritual is called from a frame loop that has the
 * head pose anyway. Before the first `observe` there is no player, and every one of those three
 * falls back to the origin-relative behaviour that shipped before: worse, but never wrong in a
 * new way.
 */
interface KnownViewer {
  readonly x: number
  readonly y: number
  readonly z: number
  readonly forward: LevelDirection
}

export class PlacementRitual {
  private phaseNow: RitualPhase
  private hit: SurfaceHit | undefined
  private placementNow: Placement
  private mode: PlacementScale
  private readonly fitNow: RoomFit
  private readonly cabinetLength: number
  private readonly barHeight: number
  private viewer: KnownViewer | undefined

  /** Whether the last surface reported was refused for standing too high. See `playable`. */
  private aimedTooHighNow = false

  /**
   * Whether the machine has stood anywhere yet this session. `stand` is the one path that turns
   * it on; a restored placement starts with it on, because that machine has already stood.
   */
  private stood: boolean

  constructor(options: PlacementRitualOptions) {
    this.cabinetLength = options.cabinetLength
    this.barHeight = options.barHeight
    this.fitNow = roomFit(options.bounds)
    this.mode = this.fitNow.recommended
    if (options.restored !== undefined) {
      this.placementNow = options.restored
      this.mode = options.restored.scale >= 1 ? 'full' : 'tabletop'
      this.phaseNow = 'adjusting'
      this.stood = true
    } else {
      this.placementNow = defaultPlacement(
        this.mode === 'full' ? 1 : TABLETOP_DEFAULT,
      )
      this.phaseNow = 'scanning'
      this.stood = false
    }
  }

  view(): RitualView {
    return {
      phase: this.phaseNow,
      reticle: this.phaseNow === 'aiming' ? this.hit : undefined,
      shimmer: this.phaseNow === 'aiming',
      placement: this.placementNow,
      scaleMode: this.mode,
      fit: this.fitNow,
    }
  }

  get phase(): RitualPhase {
    return this.phaseNow
  }

  get placement(): Placement {
    return this.placementNow
  }

  /**
   * One frame of head pose, in the session's reference space.
   *
   * Called unconditionally from the frame loop rather than only when something is about to move,
   * because the moment a placement needs a player is the moment a *menu* was pressed — and a menu
   * press is not a frame on which anybody thought to pass a head in. Storing it every frame is a
   * few assignments and removes a whole class of "recenter did nothing because nothing knew where
   * I was".
   *
   * `fx, fy, fz` is the head's forward direction; the pitch is taken out here so that every
   * consumer below is working on the floor plane.
   *
   * **Moves nothing.** Until 13 Sep 2026 this stood the machine in front of the player on every
   * frame the ritual was scanning (M5-33), and the ritual is scanning on every frame the gaze ray
   * misses the floor — so the machine swung with the head, which is the one thing XR-002c forbids.
   * The head is recorded here; `stand` is what acts on it, once.
   */
  observe(headX: number, headY: number, headZ: number, fx: number, fy: number, fz: number): void {
    void fy
    this.viewer = { x: headX, y: headY, z: headZ, forward: levelForward(fx, fz) }
  }

  /**
   * The first placement: the machine stands in front of the player the ritual has seen (XR-002c).
   *
   * Once per session, on the first head pose — a machine that has stood is not stood again, so
   * **Re-place** gets the reticle it asks for and a recycle gets the placement it had. Nothing
   * happens before a head has been observed (a placement made from the origin is the M5-33 defect
   * again), and nothing happens to a restored placement, which has already stood. Returns whether
   * it stood, so the caller can say so.
   */
  stand(): boolean {
    if (this.stood || this.phaseNow !== 'scanning' || this.viewer === undefined) return false
    this.placementNow = this.inFrontOfThePlayer(this.placementNow.scale)
    this.phaseNow = 'adjusting'
    this.hit = undefined
    this.aimedTooHighNow = false
    this.stood = true
    return true
  }

  /**
   * A remembered placement came back: the anchor said where the machine is (06 §3.1).
   *
   * The ritual takes the placement as its own and treats it as placed, so that every row on the
   * place page — a nudge, Face Me, Bring it to you — works on the machine the player is looking at
   * rather than on whatever the ritual last computed. Before this the restore path called
   * `recenter`, which does nothing while the ritual is scanning, and left the ritual holding a
   * placement the machine was not at. The scale mode is the caller's to set — it is a choice
   * recorded beside the anchor, not something a placement's scale implies.
   */
  adopt(placement: Placement): void {
    this.placementNow = placement
    this.phaseNow = 'adjusting'
    this.hit = undefined
    this.aimedTooHighNow = false
    this.stood = true
  }

  /**
   * The machine, pulled to somewhere this player could touch it.
   *
   * A no-op until somebody has been observed, and a no-op when the placement is already in reach,
   * so it is safe on every path including the ones that were already right.
   */
  private reachable(placement: Placement): Placement {
    const viewer = this.viewer
    if (viewer === undefined) return placement
    return clampToReach(placement, viewer.x, viewer.z, this.cabinetLength)
  }

  /**
   * A placement in front of the player, or the origin-relative fallback when there is no player.
   *
   * The fallback is the pre-M5-33 behaviour, kept for exactly one case: a control pressed before
   * a single head pose has arrived. It is wrong for a player standing away from the origin, which
   * is why every caller here would rather have a viewer — but it is a placement, and a machine
   * somewhere is recoverable in a way that a machine nowhere is not.
   */
  private inFrontOfThePlayer(scale: number): Placement {
    const viewer = this.viewer
    if (viewer === undefined) return defaultPlacement(scale)
    // Squared to the room when the gaze is nearly along an axis (26 Sep 2026, `squareToRoom`).
    const square = squareToRoom(viewer.forward.x, viewer.forward.z)
    return placementInFrontOf(viewer.x, viewer.z, square, scale, this.cabinetLength)
  }

  /**
   * A hit test came back. `undefined` means the runtime found nothing this frame.
   *
   * Only meaningful while scanning or aiming — once the machine is placed, a hit test wandering
   * across the room must not move it.
   */
  onSurface(hit: SurfaceHit | undefined): void {
    if (this.phaseNow !== 'scanning' && this.phaseNow !== 'aiming') return
    this.hit = hit
    const usable = hit !== undefined && this.playable(hit)
    this.aimedTooHighNow = hit !== undefined && !usable
    this.phaseNow = usable ? 'aiming' : 'scanning'
  }

  /**
   * The reticle found a surface and refused it for standing too high. See `playable`.
   *
   * False while nothing is being aimed at, so that an empty room and a refused desk are not the
   * same sentence to the player.
   */
  get aimedTooHigh(): boolean {
    return this.aimedTooHighNow
  }

  /**
   * Whether a machine standing on this surface is one the player could play.
   *
   * **The same question `barIsPlayable` asks of a restored anchor, asked here first.** A surface
   * probe answers about the room and not about the player: it will report a desk at 0.76 m, and a
   * full-size cabinet standing on one puts its lockdown bar above the eyeline of anybody in the
   * room. That placement is visible, upright, correctly scaled and completely unplayable — the
   * same failure mode `reachable` exists for, one axis over.
   *
   * **Refused rather than clamped, because height is not continuous.** Distance can be talked down
   * to any value and still make sense; a cabinet has to rest on something. There is no valid height
   * between "on the desk you aimed at" and "on the floor under it", so a clamp would invent a
   * cabinet standing in mid-air. The reticle simply does not settle there and the player aims lower.
   *
   * Two exceptions, both about not deadlocking the ritual: a player nobody has observed cannot be
   * judged against, and the floor is always allowed because it is the lowest thing a room has and
   * there is nowhere lower to send anybody.
   */
  private playable(hit: SurfaceHit): boolean {
    return this.barClears(hit.y, clampScale(this.mode, this.placementNow.scale))
  }

  /**
   * Whether a cabinet of this scale standing at this height is one the player could play.
   *
   * The predicate itself, shared by `playable` and `adjust` because placing and resizing are two
   * ways of arriving at the same unplayable cabinet, and a rule written twice is a rule that will
   * be edited once.
   */
  private barClears(y: number, scale: number): boolean {
    const viewer = this.viewer
    if (viewer === undefined) return true
    if (y <= FLOOR_TOLERANCE) return true
    return barIsPlayable(y + this.barHeight * scale, viewer.y)
  }

  /**
   * The largest scale that still leaves the bar under the eyeline, at the height it is standing at.
   *
   * `wanted` untouched whenever the rule does not apply — no player observed, or a machine on the
   * floor — so a drag in those cases is exactly what it was.
   */
  private largestPlayable(wanted: number): number {
    if (this.barClears(this.placementNow.y, wanted)) return wanted
    const viewer = this.viewer
    if (viewer === undefined || this.barHeight <= 0) return wanted
    const room = viewer.y - RESTORED_BAR_BELOW_EYE - this.placementNow.y
    return clampScale(this.mode, room / this.barHeight)
  }

  /** The pinch, or the trigger. Places the machine where the reticle is. */
  place(): boolean {
    if (this.phaseNow !== 'aiming' || this.hit === undefined) return false
    // **Asked again here, and not only through the phase.** `onSurface` refuses a surface by
    // leaving the ritual in `scanning`, which is a fact about the frame the surface arrived in;
    // the eyeline is read every frame and can fall between that frame and the pinch — sitting
    // down is exactly the motion a player makes just before reaching for a machine.
    if (!this.playable(this.hit)) return false
    // **Clamped, because a surface probe answers about the room and not about the player.** A
    // hit test will happily report the floor on the far side of a living room, and a machine
    // placed there is visible, upright, correctly scaled and completely unplayable — the failure
    // mode that reads as working. The direction the player aimed in is kept; only the distance
    // is argued with.
    this.placementNow = this.reachable({
      x: this.hit.x,
      y: this.hit.y,
      z: this.hit.z,
      yaw: this.hit.yaw ?? 0,
      scale: clampScale(this.mode, this.placementNow.scale),
    })
    this.phaseNow = 'adjusting'
    return true
  }

  /** Full or tabletop. Offered at placement and always available from the wrist menu. */
  setScaleMode(mode: PlacementScale, scale = TABLETOP_DEFAULT): void {
    this.mode = mode
    this.placementNow = { ...this.placementNow, scale: clampScale(mode, mode === 'full' ? 1 : scale) }
  }

  /**
   * A corner handle was dragged (M5-16).
   *
   * Rotate and resize only. `deltaYaw` in radians, `scaleFactor` multiplicative — a handle drag
   * is naturally proportional, and an additive scale would move the cabinet at a different rate
   * depending on how big it already was.
   */
  adjust(deltaYaw: number, scaleFactor = 1): void {
    if (this.phaseNow !== 'adjusting') return
    const wanted = clampScale(this.mode, this.placementNow.scale * scaleFactor)
    // **A handle can raise the bar as surely as a reticle can.** Refusing the surface settles the
    // question for the scale it was placed at, and then a corner drag grows a tabletop machine on
    // a table until its bar is above the player's eyeline again — the same unplayable cabinet by a
    // quieter route. Growth only: rotation is always the player's, shrinking is always allowed,
    // and it stops at the largest size that fits rather than refusing to move at all.
    const scale =
      wanted > this.placementNow.scale
        ? Math.max(this.placementNow.scale, this.largestPlayable(wanted))
        : wanted
    this.placementNow = { ...this.placementNow, yaw: this.placementNow.yaw + deltaYaw, scale }
  }

  /** A 5° nudge from the wrist menu (06 §5). */
  rotateBy(radians: number): void {
    this.placementNow = { ...this.placementNow, yaw: this.placementNow.yaw + radians }
  }

  /**
   * Face Me: turn the machine to face a head at `(x, z)` looking along `yaw`.
   *
   * Turns the cabinet toward the *player*, which is the direction from the machine to the head —
   * not the head's own facing. A player who has walked round the side of the machine and asks it
   * to face them wants it turned to them, not turned to wherever they happen to be looking.
   */
  faceMe(headX: number, headZ: number): void {
    this.placementNow = {
      ...this.placementNow,
      yaw: yawFacing(this.placementNow.x, this.placementNow.z, headX, headZ, this.placementNow.yaw),
    }
  }

  /**
   * Recenter: put the machine back in front of the player at the current scale.
   *
   * **In front of the player, which is not the same as at the origin.** This used to return the
   * cabinet to `defaultPlacement` — 1.1 m along −Z of the *tracking origin* — on the assumption
   * that the player was standing there. In a `local-floor` space the origin is wherever room
   * setup or the last platform recentre left it, and on a headset with no saved space the runtime
   * is permitted to invent one. So the control a player reaches for *because* the machine is too
   * far away was capable of sending it further away, which is the worst possible behaviour for a
   * recovery action.
   */
  recenter(headPlacement?: Placement): void {
    this.placementNow = headPlacement ?? this.inFrontOfThePlayer(this.placementNow.scale)

    // **A recentre moves the machine. It is not the player saying where the machine goes.**
    // Reported from the Quest headset (25 Aug 2026): a system recentre fired 155 ms after the room
    // arrived, before anybody had placed anything, and this line ended the ritual — no reticle
    // from then on, no pinch, and `observe` stops standing the machine in front of the player
    // because that only happens while `scanning`. Meta raises one of these on entering an
    // application, so it was neither something the player did nor something they could avoid.
    //
    // `aiming` is left alone for the same reason: a reticle sitting on a surface is a question the
    // player has not answered, and a recentre is not an answer to it.
    if (this.phaseNow === 'scanning' || this.phaseNow === 'aiming') return
    this.phaseNow = 'adjusting'
  }

  /** Re-place: back to the reticle. */
  replace(): void {
    this.phaseNow = 'scanning'
    this.hit = undefined
  }

  /**
   * The player reached for the machine rather than for a handle. Placement is over.
   *
   * Idempotent, because it is called from the frame loop the moment a flipper button or the
   * plunger is touched, and that is true on every frame the hand stays there.
   */
  startPlaying(): void {
    if (this.phaseNow === 'adjusting' || this.phaseNow === 'aiming') this.phaseNow = 'playing'
  }

  /**
   * The anchor could not be re-localised. Back to safe placement (06 §3).
   *
   * A separate entry point from `replace()` even though it does the same thing, because the two
   * mean different things to the player and the shell says different sentences about them: one is
   * a choice and the other is an apology.
   */
  anchorLost(): void {
    this.phaseNow = 'scanning'
    this.hit = undefined
    // Same reasoning as `recenter`: the safe placement has to be safe *for the player*, and the
    // origin is not a fact about where they are standing.
    this.placementNow = this.inFrontOfThePlayer(this.placementNow.scale)
  }
}
